import "react-use-audio-recorder/dist/index.css";

import { Badge, Box, Button, Container, Flex, HStack, IconButton, Textarea } from "@chakra-ui/react";
import {AxiosResponse} from "axios";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import { postAnswer, updateTimeLeft, updateWritingAnswer } from "../../shared/Api";
import AudioRecorder, {AudioRecorderStart}  from "../../shared/components/AudioRecorder";
import Markdown from "../../shared/components/Markdown";
import RenderResource, {ResourceUpdate} from "../../shared/components/RenderResource";
import RenderText from "../../shared/components/RenderText";
import TimerComponent, {TimerComponentStop} from "../../shared/components/TimerComponent";
import {useFont} from "../../shared/hooks/FontProvider";
import {useTheme} from "../../shared/hooks/ThemeProvider";
import { AnswerPropertyKey, CandidateStatus , customButtonProps, QuestionParameterKey, TestType } from "../../shared/model/Constants";
import { StatusDto, TestDto } from "../../shared/model/Dto";
import { publicKeys } from "../../shared/QueryKeys";
import { disableWritingCommands, enableWritingCommands, getUniqueOrigin, isInAvataiSite, validResourceExtension } from "../../shared/Utils";
import Options from "./Options";

type QuestionProps = {
  test: TestDto;
  status: StatusDto;
  isLoading: boolean;
  onNextQuestion: (timeLeft: number, answerText : string) => void;
  onReRender?: () => void;
  timeLeft?: MutableRefObject<number>;
};

// Rerendering this component does not rerender the entire component.
// audio elements on other components don't stop when this is rerendered
const AnswerSection = (props: QuestionProps) => {
  const theme = useTheme().theme;
  const font = useFont().font;
  const isAvatai = isInAvataiSite();

  const params = useParams<{
    publicId: string;
    candidateToken: string;
  }>();
  const publicId = params.publicId || "";
  const candidateToken = params.candidateToken || "";

  const [originalText, setOriginalText] = useState<string>("");
  const [answerText, setAnswerText] = useState<string>("");
  const [answerReady, setAnswerReady] = useState<boolean>(false);

  /*
   TODO: use map of answerProperties to handle optionIds, answerBlob, isValidBlob...
   */
  const postAnswerMutation = useMutation(
    ({ publicId, question, skillType, timeLeft, answerBlob, isValidBlob }: any) =>
      postAnswer(publicId, question, [], timeLeft, answerText, true, candidateToken, skillType)
  );
  function handleClick() {
    if (props.timeLeft && answerText) {
        props.onNextQuestion(props.timeLeft.current, answerText);
    }
    if (props.onReRender) props.onReRender();
  }

  const saveDraftQuery = useQuery(
      publicKeys.testQuestionsByPublicId(publicId, props.test.nextQuestion.id),
      async (): Promise<AxiosResponse<void> | null> => {
        if (answerText !== originalText && !answerReady) {
          setOriginalText(answerText);
          return await updateWritingAnswer(publicId, candidateToken, props.test.nextQuestion.id, answerText);
        } else return null;
      },
    {
      refetchInterval: 1000 * 120, // 2 min
      refetchIntervalInBackground: true
    }
  );

  useEffect(() => {
    if (props.test.nextQuestion?.properties && answerText==="" &&
        AnswerPropertyKey.WRITING_TEXT_ANSWER in props.test.nextQuestion.properties ) {
      setAnswerText(props.test.nextQuestion.properties[AnswerPropertyKey.WRITING_TEXT_ANSWER]);
    };
  }, [props.test.nextQuestion]);

  return ( <>
        <Textarea
                size='md'
                resize={'none'}
                rows={6}
                onChange={(event)=>setAnswerText(event.target.value)}
                disabled={answerReady}
                value={answerText}

                maxLength={15000}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />

        <Flex justify="right"  gap="5"  paddingTop={10}>
            <Button
                onClick={()=>{
                    setAnswerReady(true)
                    if (answerText !== originalText) {
                      setOriginalText(answerText);
                      updateWritingAnswer(publicId, candidateToken, props.test.nextQuestion.id, answerText);
                    }
                }}
                isLoading={props.isLoading}
                disabled={answerReady || (!answerText) || answerText.trim().length === 0}
                {...customButtonProps(isAvatai)}
                fontWeight={font.weight}
                fontSize={font.buttonText}
                colorScheme={'green'}
            >
                Confirm Answer
            </Button>
            <Button
                onClick={()=>setAnswerReady(false)}
                isLoading={props.isLoading}
                disabled={!answerReady}
                {...customButtonProps(isAvatai)}
                fontWeight={font.weight}
                fontSize={font.buttonText}
                colorScheme={'red'}
            >
                Edit Answer
            </Button>
            <Button
                onClick={handleClick}
                isLoading={props.isLoading}
                disabled={!answerReady}
                {...customButtonProps(isAvatai)}
                fontWeight={font.weight}
                fontSize={font.buttonText}
            >
                Continue
            </Button>
        </Flex>
  </>)
}

const WritingQuestion = (props: QuestionProps) => {
  const theme = useTheme().theme;
  const timeLeft = useRef<number>(props.test.timeLeft);
  const [reRender, setReRender] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const params = useParams<{
    publicId: string;
    candidateToken: string;
  }>();
  const publicId = params.publicId || "";
  const candidateToken = params.candidateToken || "";

/*
  useEffect(() => {
    enableWritingCommands();
    return () => {
      disableWritingCommands();
    };
  }, []);
*/
  // For questions that have text with bold and non-bold sections
  let parsedText=
    props.test.nextQuestion.statement ? (props.test.nextQuestion.statement.startsWith('**') ?
      `${props.test.nextQuestion.statement.slice(2)}` :
      `**${props.test.nextQuestion.statement}` ) : "";

  parsedText = parsedText.endsWith('**') ?
    parsedText.slice(0,parsedText.length-2) :
    parsedText+'**';

  const postUpdateTimeLeftMutation = useMutation(
    ({ timeLeft }: any) =>
      updateTimeLeft(publicId, candidateToken, timeLeft),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(publicKeys.testsByPublicId(publicId)),
    }
  );


  const handleReRender = () => {
    setReRender(!reRender);
  };

    return (
      <Container paddingInline="5" maxW="max" aria-hidden="true">
        {/* props.test.timeLimit !== 0 &&
        <TimerComponent timeLeft={timeLeft}
                        onTimerFinished={(timeLeft: number) => {
                          // only ends test if it is single adaptive, not multistage
                          //if (props.test.testType === TestType.ADAPTIVE &&
                          //  (props.test.conditions && props.test.conditions.includes("ADAPTIVE"))
                          //)
                            postUpdateTimeLeftMutation.mutate({ timeLeft });
                          }
                        }
                        />
        */}
        <Flex justify="center" align="space-between" direction="column">
            <Box width="2xl"
                padding="2"
                borderWidth="1px"
                borderRadius="lg"
                borderColor={theme.OUTLINE}
                borderStyle="solid"
                >
                <HStack><Badge alignSelf="start">#{props.test.status.answered + 1}</Badge>
                <Box width="xl">
                    { props.test.nextQuestion.instructions != null &&
                      <RenderText id={props.test.nextQuestion.instructions}
                                  content={`**${props.test.nextQuestion.instructions}**`}
                                   />
                    }
                </Box>
                </HStack>
                { validResourceExtension(props.test.nextQuestion.body)
                    ? <RenderResource
                        resource={props.test.nextQuestion.body}
                        subdirectory={"Tests/"+props.test.publicId}
                        conditions={props.test.conditions}
                        small={true}
                    />
                    : <Markdown content={props.test.nextQuestion.body} />
                }

                { props.test.nextQuestion.statement != null &&
                    <RenderText id={props.test.nextQuestion.statement} debug={true}
                                content={parsedText} />
                }
            </Box>
            <Box width="2xl"
                 padding="2"
                 marginBlock="2"
                 borderWidth="1px"
                 borderRadius="lg"
                 borderColor={theme.OUTLINE}
                 borderStyle="solid"
                 >
              <AnswerSection  isLoading={props.isLoading}
                               onNextQuestion={props.onNextQuestion}
                               status={props.status}
                               test={props.test}
                               onReRender={handleReRender}
                               timeLeft={timeLeft}
              />
            </Box>
        </Flex>
      </Container>
    );
};

export default WritingQuestion;